import React, { FC, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { AuthenticationType, Group, MailboxFolder, ResponseData, UnsavedGroup } from "../../../../../models";
import NumberInput from "../../shared/fields/numberInput";
import TextareaInput from "../../shared/fields/textareaInput";
import TextInput from "../../shared/fields/textInput";
import ToggleInput from "../../shared/fields/toggleInput";
import MailboxFoldersInput from "../../shared/fields/mailboxFoldersInput";
import SettingsFormInfo from "../../shared/settingsFormInfo";
import SettingsTitle from "../../shared/settingsTitle";
import { putGroup } from "../../../../../services/put.data.service";
import { postGroup } from "../../../../../services/post.data.service";
import SelectInput from "../../shared/fields/selectInput";
import { getActionClassifyEmail, getActionExtractAccountData, getActionExtractTransferFormData, getPollingAddressesForFolders, getTimezones } from "../../../../../services/get.data.service";
import { SelectOption } from "../../../../../models/selectOption";
import { Typeahead } from "react-bootstrap-typeahead";
import SettingsSubmitButton from "../../shared/settingsSubmitButton";

type Props = {
  isDuplicate: (fieldToCheck: keyof Group, value: string)=> boolean;
  existingGroup?: Group;
  load: ()=>void;
};

const GroupForm: FC<Props> = ({ isDuplicate, existingGroup, load }) => {
  const [redirect, setRedirect] = useState<boolean>(false);
  const [actionClassifyEmailOptions, setActionClassifyEmailOptions] = useState<SelectOption[]>([]);
  const [actionExtractAccountDataOptions, setActionExtractAccountDataOptions] = useState<SelectOption[]>([]);
  const [actionExtractTransferFormDataOptions, setActionExtractTransferFormDataOptions] = useState<SelectOption[]>([]);
  const [emailAccountOptions, setEmailAccountOptions] = useState<SelectOption[]>([]);
  const [groupCode, setGroupCode] = useState<string>("");
  const [businessName, setBusinessName] = useState<string>("");
  const [pollingPaused, setPollingPaused] = useState<boolean>(false);
  const [acknowledgementEmailBody, setAcknowledgementEmailBody] = useState<string>("");
  const [sendEmailFooterText, setSendEmailFooterText] = useState<string>("");
  const [suppressAcknowledgement, setSuppressAcknowledgement] = useState<boolean>(true);
  const [sendToStudio, setSendToStudio] = useState<boolean>(true);
  const [numberOfDaysToKeepClosedCases, setNumberOfDaysToKeepClosedCases] = useState<number>(0);
  const [authenticationType, setAuthenticationType] = useState<AuthenticationType>(AuthenticationType.Azure);
  const [studioUrl, setStudioUrl] = useState<string>("");
  const [studioClientId, setStudioClientId] = useState<string>("");
  const [sendImagesToStudio, setSendImagesToStudio] = useState<boolean>(false);
  const [studioApiVersion, setStudioApiVersion] = useState<number>(1);
  const [studioTimezone, setStudioTimezone] = useState<string>("UTC");
  const [sendTransferFormsToStudio, setSendTransferFormsToStudio] = useState<boolean>(false);
  const [actionClassifyEmailId, setActionClassifyEmailId] = useState<number | null>(null);
  const [actionExtractAccountDataId, setActionExtractAccountDataId] = useState<number | null>(null);
  const [actionExtractTransferFormDataId, setActionExtractTransferFormDataId] = useState<number | null>(null);
  const [closeWithoutSending, setCloseWithoutSending] = useState<boolean>(false);
  const [deleteFromOutlook, setDeleteFromOutlook] = useState<boolean>(false);
  const [moveToMailboxFolderEmailAddress, setMoveToMailboxFolderEmailAddress] = useState<string | null>(null);
  const [moveToMailboxFolder, setMoveToMailboxFolder] = useState<MailboxFolder|null>(null);
  const [timezones, setTimezones] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (existingGroup) {
      setGroupCode(existingGroup.groupCode);
      setBusinessName(existingGroup.businessName);
      setPollingPaused(existingGroup.pollingPaused);
      setAcknowledgementEmailBody(existingGroup.acknowledgementEmailBody.replaceAll("<br />", "\n"));
      setSendEmailFooterText(existingGroup.sendEmailFooterText.replaceAll("<br />", "\n"));
      setSendToStudio(existingGroup.sendToStudio);
      setSuppressAcknowledgement(existingGroup.suppressAcknowledgement);
      setNumberOfDaysToKeepClosedCases(existingGroup.numberOfDaysToKeepClosedCases);
      setAuthenticationType(existingGroup.authenticationType);
      setStudioUrl(existingGroup.studioUrl);
      setStudioClientId(existingGroup.studioClientId);
      setSendImagesToStudio(existingGroup.sendImagesToStudio);
      setStudioApiVersion(existingGroup.studioApiVersion || 1);
      setStudioTimezone(existingGroup.studioTimezone || "UTC");
      setSendTransferFormsToStudio(existingGroup.sendTransferFormsToStudio);
      setActionClassifyEmailId(existingGroup.actionClassifyEmailId);
      setActionExtractAccountDataId(existingGroup.actionExtractAccountDataId);
      setActionExtractTransferFormDataId(existingGroup.actionExtractTransferFormDataId);
      setCloseWithoutSending(existingGroup.closeWithoutSending);
      setDeleteFromOutlook(existingGroup.deleteFromOutlook ?? false);
      setMoveToMailboxFolderEmailAddress(existingGroup.moveToMailboxFolderEmailAddress ?? null);
      setMoveToMailboxFolder(existingGroup.moveToMailboxFolderId ? { 
        id: existingGroup.moveToMailboxFolderId, 
        name: existingGroup.moveToMailboxFolderName || "Inbox"
      } : null);
    }
    getActionClassifyEmail().then((response) => {
      setActionClassifyEmailOptions(
        response.data
      );
    });
    getActionExtractAccountData().then((response) => {
      setActionExtractAccountDataOptions(
        response.data
      );
    });
    getActionExtractTransferFormData().then((response) => {
      setActionExtractTransferFormDataOptions(
        response.data
      );
    });
    getPollingAddressesForFolders().then((response) => {
      const addresses = response.success && Array.isArray(response.data) ? response.data : [];
      setEmailAccountOptions(addresses.map((emailAddress, index) => ({ id: index + 1, label: emailAddress })));
      });
    getTimezones().then((response) => {
      setTimezones(response.data);
    });
  }, [existingGroup]);

  const validate = (): string[] => {
    const errorMessages: string[] = [];
    if (groupCode.length === 0) {
      errorMessages.push("Group Code is required");
    } else {
      if (isDuplicate("groupCode", groupCode)) {
        errorMessages.push("Group Code has already been used");
      }
    }
    if (businessName.length === 0) {
      errorMessages.push("Business Name is required");
    } else {
      if (isDuplicate("businessName", businessName)) {
        errorMessages.push("Business Name has already been used");
      }
    }
    if (!suppressAcknowledgement && acknowledgementEmailBody.length === 0) {
      errorMessages.push("Acknowledgement Email Body Text is required when Suppress ACK is off");
    }
    if (sendToStudio) {
      if (!studioUrl) {
        errorMessages.push("Studio Url is required when Send to Studio is on");
      } else {
        try {
          const url = new URL(studioUrl);
          if (url.protocol !== "http:" && url.protocol !== "https:") {
            errorMessages.push("Studio Url is not an acceptable URL");
          }
        } catch(err) {
          errorMessages.push("Studio Url is not a valid URL");
        }
      }
      if (!studioClientId) {
        errorMessages.push("Studio Client Id is required when Send to Studio is on");
      }
      if (!studioTimezone) {
        errorMessages.push("Studio Timezone is required when Send to Studio is on");
      } else {
        if (!timezones.includes(studioTimezone)) {
          errorMessages.push("Studio Timezone is not a valid timezone");
        }
      }
    }
    return errorMessages;
  };
  
  const handleSaveResponse = (result: ResponseData<Group>): void => {
    if (result.success) {
      load(); 
      setRedirect(true);
    } else {
      setErrors([`Failed to update. ${result.message || result}`]);
    }
  };

  const save = async (): Promise<void> => {
    const errorMessages = validate();
    setErrors(errorMessages);
    if (!errorMessages.length) {
      const toSave: UnsavedGroup = {
        groupCode: groupCode.trim(),
        businessName: businessName.trim(),
        pollingPaused,
        acknowledgementEmailBody: suppressAcknowledgement ? "" : acknowledgementEmailBody.replaceAll("\n", "<br />"),
        sendEmailFooterText: suppressAcknowledgement ? "" : sendEmailFooterText.replaceAll("\n", "<br />"),
        sendToStudio,
        suppressAcknowledgement,
        numberOfDaysToKeepClosedCases,
        authenticationType,
        studioUrl: studioUrl ? studioUrl.trim() : "",
        studioClientId,
        sendImagesToStudio,
        studioApiVersion: studioApiVersion,
        studioTimezone,
        sendTransferFormsToStudio,
        actionClassifyEmailId: sendToStudio ? null : actionClassifyEmailId,
        actionExtractAccountDataId: sendToStudio ? actionExtractAccountDataId : null,
        actionExtractTransferFormDataId,
        closeWithoutSending: sendToStudio ? false : closeWithoutSending,
        deleteFromOutlook: sendToStudio ? false : deleteFromOutlook,
        moveToMailboxFolderEmailAddress: sendToStudio ? moveToMailboxFolderEmailAddress : null,
        moveToMailboxFolderId: sendToStudio && moveToMailboxFolder ? moveToMailboxFolder.id : null,
        moveToMailboxFolderName: sendToStudio && moveToMailboxFolder ? moveToMailboxFolder.name : null,
      };
      if (existingGroup) {
        return putGroup(existingGroup.groupId, toSave).then((result: ResponseData<Group>)=>{          
          handleSaveResponse(result);
        });
      } else {
        return postGroup(toSave).then((result: ResponseData<Group>)=>{
          handleSaveResponse(result);
        });
      }
    }
  };

  if (redirect) return <Redirect to="/system-settings/groups/list" />;

  return (
    <>
      <SettingsTitle errors={errors} title={existingGroup ? "Edit Group" : "Create Group"}>
        <SettingsSubmitButton onSave={save} />
      </SettingsTitle>
      <div className="row">
        <SettingsFormInfo existingTitle={existingGroup?.groupCode} >
          {existingGroup?.businessName}
        </SettingsFormInfo>
        <div className="col-sm-7">
          <div className="content-container p-3">
            {!existingGroup && <TextInput 
              label="Group Code" 
              name="group-code" 
              onChange={setGroupCode} 
              value={groupCode} 
            />
            }
            <TextInput 
              label="Business Name" 
              name="business-name" 
              onChange={setBusinessName} 
              value={businessName} 
            />
            <ToggleInput
              label="Polling Paused"
              name="polling-paused"
              onChange={setPollingPaused}
              value={pollingPaused}
            />
            <hr />
            <ToggleInput
              label="Suppress ACK"
              name="suppress-ack"
              onChange={setSuppressAcknowledgement}
              value={suppressAcknowledgement}
            />
            {!suppressAcknowledgement && 
              <>
                <TextareaInput               
                  label={<>
                    <span>Acknowledgement Email Body Text</span>
                    <br />
                    <small>
                      <strong>The string &lsquo;{"{caseReference}"}&rsquo; will be replaced by the actual Case Reference</strong>
                    </small>
                  </>} 
                  name="acknowledgement-email-body-test" 
                  onChange={setAcknowledgementEmailBody} 
                  rows={8}
                  value={acknowledgementEmailBody} 
                />
                <TextareaInput 
                  label="Send Email Footer Text" 
                  name="send-email-footer-text" 
                  onChange={setSendEmailFooterText} 
                  value={sendEmailFooterText} 
                />
              </>
            }
            <hr />
            {!sendToStudio && 
              <>
                <ToggleInput
                  label="Close Without Sending"
                  name="close-without-sending"
                  onChange={setCloseWithoutSending}
                  value={closeWithoutSending}
                />
                <ToggleInput
                  label="Delete From Outlook"
                  name="delete-from-outlook"
                  onChange={setDeleteFromOutlook}
                  value={deleteFromOutlook}
                />
              </> 
            }
            {!sendToStudio && actionClassifyEmailOptions.length > 0 &&
              <>
                <SelectInput
                  label="Action: Classify Emails for:"
                  name="action-classify-email"
                  onChange={setActionClassifyEmailId}
                  id={actionClassifyEmailId}
                  options={actionClassifyEmailOptions}
                  nullOption="Don't classify emails"
                />
                <hr />
              </>
            }
            <ToggleInput
              label="Send To Studio"
              name="send-to-studio"
              onChange={(state: boolean) => {
                if (state) {
                  setActionClassifyEmailId(null);
                  setCloseWithoutSending(false);
                  setDeleteFromOutlook(false);
                } else {
                  setStudioUrl("");
                  setStudioClientId("");
                  setStudioTimezone("UTC");
                  setActionExtractAccountDataId(null);
                  setDeleteFromOutlook(false);
                  setMoveToMailboxFolderEmailAddress(null);
                  setMoveToMailboxFolder(null);
                }
                setSendToStudio(state)
              }}
              value={sendToStudio}
            />
            {sendToStudio && 
              <>
                <hr />
                <SelectInput
                  label="Authentication Type"
                  name="authentication-type"
                  onChange={(id: number | null) => id !== null && setAuthenticationType(id as AuthenticationType)}

                  id={authenticationType}
                  options={[
                    {id: AuthenticationType.Azure, label: "Azure"},
                    {id: AuthenticationType.Keycloak, label: "Keycloak"}
                  ]}
                  notNull
                />
                <TextInput 
                  label="Studio Url e.g. https://vanguard.appway.com/secure/api/AwIndexingTriaging/integration/v1/triage" 
                  name="studio-url" 
                  onChange={setStudioUrl} 
                  value={studioUrl || ""} 
                />
                <TextInput 
                  label="Studio Client Id" 
                  name="studio-client-id" 
                  onChange={setStudioClientId} 
                  value={studioClientId || ""} 
                />
                <div className="form-group">
                  <label htmlFor="studio-timezone">Studio Timezone</label>
                  <Typeahead
                    id="studio-timezone"
                    onChange={(value) =>{
                      if (value.length > 0) setStudioTimezone(value[0])
                    }}
                    onInputChange={(value) => {
                      setStudioTimezone(value)
                    }}
                    selected={[studioTimezone]}
                    options={timezones}
                  />
                </div>
                <ToggleInput
                  label="Send Images To Studio"
                  name="send-images-to-studio"
                  onChange={(state: boolean)=> {
                    setSendImagesToStudio(state);
                  }}
                  value={sendImagesToStudio}
                />
                <SelectInput
                  label="Studio API Version"
                  name="studio-api-version"
                  onChange={(id: number|null)=>id && setStudioApiVersion(id)}
                  id={studioApiVersion}
                  options={[
                    {id: 1, label: "Version 1"}, 
                    {id: 2, label: "Version 2"}
                  ]}
                  notNull
                />
                {actionExtractAccountDataOptions.length > 0 && 
                  <SelectInput
                    label="Extract Account Data for Platform:"
                    name="action-extract-account-data"
                    onChange={setActionExtractAccountDataId}
                    id={actionExtractAccountDataId}
                    options={actionExtractAccountDataOptions}
                    nullOption="Don't extract account data"
                  />
                }
                <SelectInput
                  label="Move To Mailbox Folder Email Account"
                  name="move-to-mailbox-folder-email-account"
                  onChange={(id: number|null) => { 
                    setMoveToMailboxFolderEmailAddress(emailAccountOptions.find((item) => item.id === id)?.label || null );
                    setMoveToMailboxFolder(null);
                  }}
                  id={emailAccountOptions.find((item) => item.label === moveToMailboxFolderEmailAddress)?.id || null}
                  options={emailAccountOptions}
                  nullOption="Don't move to mailbox folder"
                />
                {moveToMailboxFolderEmailAddress && (
                  <MailboxFoldersInput
                    buttonText={moveToMailboxFolder?.name ? `Change folder to move to, currently "${moveToMailboxFolder?.name}"` : "Set folder to move to"}
                    emailAddress={moveToMailboxFolderEmailAddress}
                    mailboxFolder={moveToMailboxFolder}
                    setMailboxFolder={setMoveToMailboxFolder}
                  />
                )}
              </>
            }
            <hr />
            {actionExtractTransferFormDataOptions.length > 0 &&
              <SelectInput
                label="Extract Transfer Form Data for Client:"
                name="action-extract-transfer-form-data"
                onChange={setActionExtractTransferFormDataId}
                id={actionExtractTransferFormDataId}
                options={actionExtractTransferFormDataOptions}
                nullOption="Don't extract transfer form data"
              />
            }
            {actionExtractTransferFormDataId !== null &&                 
              <ToggleInput
                label="Send Transfer Forms To Studio"
                name="send-transfer-forms-to-studio"
                onChange={setSendTransferFormsToStudio}
                value={sendTransferFormsToStudio}
              />
            }
            <NumberInput
              label="Number of Days to Keep Closed Cases (0 means no cases will be deleted)"
              name="number-of-days"
              onChange={(value: number):void => {if (value > -1) setNumberOfDaysToKeepClosedCases(value)}}
              value={numberOfDaysToKeepClosedCases}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default GroupForm;
